import { inject, Injectable } from '@angular/core';
import { TenantPrivilegesResource } from '../../../resources/tenant-privileges.resource';
import {
  GroupGlobalPrivilegesFormData,
  GroupTenantPrivilegesFormData,
  PrivilegeRestriction,
  PrivilegeRole,
  Privileges,
  PrivilegeScope,
  PrivilegesFormData,
  PrivilegeType,
  UserGlobalPrivilegesFormData,
  UserTenantPrivilegesFormData,
} from './privilege-models';
import { Observable } from 'rxjs';
import {
  GroupGlobalPermissions,
  GroupTenantPermissions,
  RestrictionEntityType,
  TenantPrivilegeAssignment,
  UserGlobalPermissions,
  UserTenantPermissions,
  ZpmPrivilege,
  ZpmRole,
} from '../../../../shared/models/generated/ziroModels';
import { map } from 'rxjs/operators';
import { ToastService } from '../../../../shared/services/toast.service';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import {
  UserGroupPrivilegesModalComponent,
  UserGroupPrivilegesModalProperties,
} from './user-group-privileges-modal/user-group-privileges-modal.component';
import { ZiroModalService } from '../../../../shared/services/ziro-modal.service';
import { TranslateService } from '@ngx-translate/core';
import { GlobalPrivilegesResource } from '../../../resources/global-privileges.resource';

@Injectable({ providedIn: 'root' })
export class UserGroupPrivilegesService {
  private _toastService = inject(ToastService);
  private _ziroModalService = inject(ZiroModalService);
  private _translateService = inject(TranslateService);
  private _tenantPrivilegesResource = inject(TenantPrivilegesResource);
  private _globalPrivilegesResource = inject(GlobalPrivilegesResource);

  savePrivileges(entity: PrivilegesFormData, tenantId: string, isExisting: boolean): Observable<void> {
    if (entity.scope === PrivilegeScope.TENANT) {
      if (entity.type === PrivilegeType.USER) {
        return this._saveUserTenantPrivileges(entity, tenantId, isExisting);
      } else if (entity.type === PrivilegeType.GROUP) {
        return this._saveGroupTenantPrivileges(entity, tenantId, isExisting);
      }
    } else if (entity.scope === PrivilegeScope.GLOBAL) {
      if (entity.type === PrivilegeType.USER) {
        return this._saveUserGlobalPrivileges(entity, isExisting);
      } else if (entity.type === PrivilegeType.GROUP) {
        return this._saveGroupGlobalPrivileges(entity, isExisting);
      }
    }
    throw Error('Could not save privileges, scope was not defined.');
  }

  openModal(modalProperties: UserGroupPrivilegesModalProperties): void {
    const titleTkey = modalProperties.isExisting
      ? 'tkey;admin.user_group_privileges_modal.title.edit'
      : 'tkey;admin.user_group_privileges_modal.title.add';
    const options = {
      size: 'lg',
      modalViewProperties: {
        title: this._translateService.instant(titleTkey, {
          type: this._translateService.instant(this.humanReadableType(modalProperties.entity.type)),
        }),
        modalBodyClass: 'pb-0',
        ...modalProperties,
      },
      bodyClass: UserGroupPrivilegesModalComponent,
    };

    this._ziroModalService.openDetailedModal(() => options.modalViewProperties, options).subscribe();
  }

  humanReadableType(type: PrivilegeType): string {
    switch (type) {
      case PrivilegeType.USER:
        return 'tkey;admin.privileges.table.type.user';
      case PrivilegeType.GROUP:
        return 'tkey;admin.privileges.table.type.group';
    }
  }

  humanReadableRole(role: PrivilegeRole): string {
    switch (role) {
      case PrivilegeRole.GLOBAL_ADMINISTRATOR:
        return 'tkey;admin.privileges.table.role.global_administrator';
      case PrivilegeRole.TENANT_ADMINISTRATOR:
        return 'tkey;admin.privileges.table.role.tenant_administrator';
      case PrivilegeRole.CUSTOM:
        return 'tkey;admin.privileges.table.role.custom';
    }
  }

  getModalFormDataPrivileges(entityPrivileges: ZpmPrivilege[]): Privileges {
    const privileges: Privileges = this.defaultPrivileges();
    entityPrivileges.forEach((privilege) => {
      const privilegeKey = this._getPrivilege(privilege);
      if (privilegeKey) {
        // some of the enums in ZpmPrivilege are not managed by us
        privileges[privilegeKey] = true;
      }
    });
    return privileges;
  }

  defaultPrivileges(): Privileges {
    return {
      teamsUserManagement: false,
      resourceAccountManagement: false,
      autoAttendantManagement: false,
      callQueueManagement: false,
      emergencyLocationManagement: false,
      configurationReports: false,
      auditReports: false,
      cdrReports: false,
      migration: false,
      automation: false,
    };
  }

  private _getPrivilege(zpmPrivilege: ZpmPrivilege): keyof Privileges {
    switch (zpmPrivilege) {
      case ZpmPrivilege.TEAMS_USER_MANAGEMENT:
        return 'teamsUserManagement';
      case ZpmPrivilege.RESOURCE_ACCOUNT_MANAGEMENT:
        return 'resourceAccountManagement';
      case ZpmPrivilege.AUTO_ATTENDANT_MANAGEMENT:
        return 'autoAttendantManagement';
      case ZpmPrivilege.CALL_QUEUE_MANAGEMENT:
        return 'callQueueManagement';
      case ZpmPrivilege.EMERGENCY_LOCATION_MANAGEMENT:
        return 'emergencyLocationManagement';
      case ZpmPrivilege.CONFIG_REPORTS:
        return 'configurationReports';
      case ZpmPrivilege.AUDIT_REPORTS:
        return 'auditReports';
      case ZpmPrivilege.CDR_REPORTS:
        return 'cdrReports';
      case ZpmPrivilege.MIGRATIONS:
        return 'migration';
      case ZpmPrivilege.AUTOMATION:
        return 'automation';
      default:
        return null;
    }
  }

  private _saveUserGlobalPrivileges(entity: UserGlobalPrivilegesFormData, isExisting: boolean): Observable<void> {
    const reqBody: UserGlobalPermissions = {
      upn: entity.upn,
      ...this._getGlobalPrivilegeData(entity),
    };
    const req: Observable<void | UserGlobalPermissions> = isExisting
      ? this._globalPrivilegesResource.putUser(reqBody)
      : this._globalPrivilegesResource.postUser(reqBody);
    return req.pipe(
      map(() => {
        this._toastService.pushSaveToast(
          'tkey;admin.user_group_privileges_modal.user.toast.title',
          entity.upn,
          ZiroIcons.SAVE_SUCCESS,
        );
      }),
    );
  }

  private _saveGroupGlobalPrivileges(entity: GroupGlobalPrivilegesFormData, isExisting: boolean): Observable<void> {
    const reqBody: GroupGlobalPermissions = {
      displayName: entity.displayName,
      groupTenantGuid: entity.tenantGuid,
      ...this._getGlobalPrivilegeData(entity),
    };
    const req: Observable<void | GroupGlobalPermissions> = isExisting
      ? this._globalPrivilegesResource.putGroup(reqBody)
      : this._globalPrivilegesResource.postGroup(reqBody);
    return req.pipe(
      map(() => {
        this._toastService.pushSaveToast(
          'tkey;admin.user_group_privileges_modal.group.toast.title',
          entity.displayName,
          ZiroIcons.SAVE_SUCCESS,
        );
      }),
    );
  }

  private _getGlobalPrivilegeData(entity: UserGlobalPrivilegesFormData | GroupGlobalPrivilegesFormData): {
    roles: ZpmRole[];
    privileges: ZpmPrivilege[];
  } {
    const roles = this._getZpmRoles(entity.role);
    let privileges: ZpmPrivilege[] = [];
    if (roles.length === 0) {
      privileges = this._getZpmPrivileges(entity);
    }
    return {
      roles: roles,
      privileges: privileges,
    };
  }

  private _saveUserTenantPrivileges(
    entity: UserTenantPrivilegesFormData,
    tenantId: string,
    isExisting: boolean,
  ): Observable<void> {
    const reqBody: UserTenantPermissions = {
      upn: entity.upn,
      ...this._getTenantPrivilegeData(entity),
    };
    const req: Observable<void | UserTenantPermissions> = isExisting
      ? this._tenantPrivilegesResource.putUser(tenantId, reqBody)
      : this._tenantPrivilegesResource.postUser(tenantId, reqBody);
    return req.pipe(
      map(() => {
        this._toastService.pushSaveToast(
          'tkey;admin.user_group_privileges_modal.user.toast.title',
          entity.upn,
          ZiroIcons.SAVE_SUCCESS,
        );
      }),
    );
  }

  private _saveGroupTenantPrivileges(
    entity: GroupTenantPrivilegesFormData,
    tenantId: string,
    isExisting: boolean,
  ): Observable<void> {
    const reqBody: GroupTenantPermissions = {
      displayName: entity.displayName,
      groupTenantGuid: entity.tenantGuid,
      ...this._getTenantPrivilegeData(entity),
    };
    const req: Observable<void | GroupTenantPermissions> = isExisting
      ? this._tenantPrivilegesResource.putGroup(tenantId, reqBody)
      : this._tenantPrivilegesResource.postGroup(tenantId, reqBody);
    return req.pipe(
      map(() => {
        this._toastService.pushSaveToast(
          'tkey;admin.user_group_privileges_modal.group.toast.title',
          entity.displayName,
          ZiroIcons.SAVE_SUCCESS,
        );
      }),
    );
  }

  private _getTenantPrivilegeData(entity: UserTenantPrivilegesFormData | GroupTenantPrivilegesFormData): {
    roles: ZpmRole[];
    privileges: TenantPrivilegeAssignment[];
  } {
    const roles = this._getZpmRoles(entity.role);
    let tenantPrivilegeAssignments: TenantPrivilegeAssignment[] = [];
    if (roles.length === 0) {
      const basePrivileges = this._getZpmPrivileges(entity);
      tenantPrivilegeAssignments = this._getTenantPrivilegeAssignments(basePrivileges, entity);
    }
    return {
      roles: roles,
      privileges: tenantPrivilegeAssignments,
    };
  }

  private _getTenantPrivilegeAssignments(
    basePrivileges: ZpmPrivilege[],
    entity: UserTenantPrivilegesFormData | GroupTenantPrivilegesFormData,
  ): TenantPrivilegeAssignment[] {
    return basePrivileges.map((privilege): TenantPrivilegeAssignment => {
      if (
        privilege === ZpmPrivilege.TEAMS_USER_MANAGEMENT &&
        entity.teamsUserManagementRestrictedDialPlanGroupsAccess === PrivilegeRestriction.PARTIAL_ACCESS
      ) {
        return {
          privilege: privilege,
          restricted: true,
          restrictions: entity.teamsUserManagementRestrictedDialPlanGroups.map((option) => {
            return {
              entityId: option.value,
              entityType: RestrictionEntityType.DIAL_PLAN_GROUP,
            };
          }),
        };
      }
      return {
        privilege: privilege,
        restricted: false,
        restrictions: [],
      };
    });
  }

  private _getZpmPrivileges(entity: Privileges): ZpmPrivilege[] {
    const privileges: ZpmPrivilege[] = [];
    if (entity.teamsUserManagement) {
      privileges.push(ZpmPrivilege.TEAMS_USER_MANAGEMENT);
    }
    if (entity.resourceAccountManagement) {
      privileges.push(ZpmPrivilege.RESOURCE_ACCOUNT_MANAGEMENT);
    }
    if (entity.autoAttendantManagement) {
      privileges.push(ZpmPrivilege.AUTO_ATTENDANT_MANAGEMENT);
    }
    if (entity.callQueueManagement) {
      privileges.push(ZpmPrivilege.CALL_QUEUE_MANAGEMENT);
    }
    if (entity.emergencyLocationManagement) {
      privileges.push(ZpmPrivilege.EMERGENCY_LOCATION_MANAGEMENT);
    }
    if (entity.configurationReports) {
      privileges.push(ZpmPrivilege.CONFIG_REPORTS);
    }
    if (entity.auditReports) {
      privileges.push(ZpmPrivilege.AUDIT_REPORTS);
    }
    if (entity.cdrReports) {
      privileges.push(ZpmPrivilege.CDR_REPORTS);
    }
    if (entity.migration) {
      privileges.push(ZpmPrivilege.MIGRATIONS);
    }
    if (entity.automation) {
      privileges.push(ZpmPrivilege.AUTOMATION);
    }
    return privileges;
  }

  private _getZpmRoles(entity: PrivilegeRole): ZpmRole[] {
    switch (entity) {
      case PrivilegeRole.GLOBAL_ADMINISTRATOR:
        return [ZpmRole.GLOBAL_ADMIN];
      case PrivilegeRole.TENANT_ADMINISTRATOR:
        return [ZpmRole.TENANT_ADMIN];
      case PrivilegeRole.CUSTOM:
      default:
        return [];
    }
  }
}
